import { useEffect, useRef } from 'react'

import { clsx } from 'clsx'

import { useBlock, useSlate } from '@tribeplatform/react-slate-kit/hooks'
import {
  BlockWrapperComponent,
  SlateMode,
  UnknownProps,
} from '@tribeplatform/react-slate-kit/types'
import { useDocument } from '@tribeplatform/react-ui-kit/hooks'

import { BlockActions, BlockActionsProps } from '../SlateEditor/BlockActions.js'
import { EditorBlockExtraContext, EditorSlateExtraContext } from '../types.js'
import { customizerPreviewScrollTo } from './utils.js'

const Overlay = ({
  selected,
  focused,
  mode,
  ...blockActionProps
}: BlockActionsProps & {
  selected: boolean
  focused: boolean
  mode: SlateMode
}) => {
  if (mode === 'read-only') {
    return <div className={clsx('absolute inset-0 z-10')} />
  }
  return (
    <div
      className={clsx(
        'absolute inset-0 z-10',
        (selected || focused) && [
          'rounded-base ring-inset ring-offset-0 ring ring-customizer-blue',
        ],
      )}
    >
      <div className="flex justify-end p-2">
        <BlockActions
          {...blockActionProps}
          size="md"
          className="p-2 rounded-base bg-background-selected shadow-md border border-line"
        />
      </div>
    </div>
  )
}

const isChildNull = children => {
  try {
    return Boolean(children.type() === null)
  } catch (e) {
    return false
  }
}

export const BlockWrapper: BlockWrapperComponent = ({
  Block,
  childrenBlocks,
}) => {
  const { document } = useDocument()
  const {
    mode,
    extraContext: {
      slate: {
        focusBlock,
        unfocusBlock,
        selectBlock,
        unselectBlock,
        activateBlock,
      },
    },
  } = useSlate<EditorSlateExtraContext, EditorBlockExtraContext>()
  const {
    id,
    block: {
      compiledProps,
      config: {
        displayName,
        hide,
        editable,
        removable,
        acceptsChildren,
        lockedChildren,
      },
    },
    extraContext: { focused, selected, activated },
  } = useBlock<UnknownProps, EditorBlockExtraContext>()
  const ref = useRef<HTMLDivElement>()
  useEffect(() => {
    if (ref.current && selected && mode === 'edit') {
      customizerPreviewScrollTo(ref.current, document)
    }
  }, [selected, ref, mode, document])

  const renderedComponent = <Block {...compiledProps}>{childrenBlocks}</Block>
  const renderingEmpty = isChildNull(renderedComponent)

  const clickable = mode === 'edit' && !selected

  return (
    <div
      ref={ref}
      className={clsx(
        'relative',
        clickable && 'cursor-pointer',
        activated && 'z-40 bg-background',
      )}
      onMouseEnter={() => focusBlock(id)}
      onMouseLeave={() => unfocusBlock(id)}
      onClick={() => {
        if (clickable) {
          if (selected) {
            unselectBlock(id)
          } else {
            selectBlock(id)
          }
        }
      }}
      onDoubleClick={() => {
        if (mode === 'edit') {
          activateBlock(id)
        }
      }}
    >
      <div
        className={clsx(
          'min-h-[40px] isolate',
          renderingEmpty &&
            'bg-surface text-content-subdued rounded-lg flex items-center px-4 py-2',
        )}
      >
        {/* eslint-disable-next-line no-nested-ternary */}
        {hide ? (
          <div className="py-10 text-center mb-5 bg-surface rounded-base">{`${displayName}: Hidden`}</div>
        ) : renderingEmpty ? (
          <span className="text-sm font-medium">{displayName} - Block</span>
        ) : (
          renderedComponent
        )}
      </div>
      <Overlay
        id={id}
        mode={mode}
        selected={selected}
        focused={focused}
        editable={editable}
        removable={removable}
        acceptsChildren={acceptsChildren && !lockedChildren}
        hide={!focused}
      />
    </div>
  )
}
